<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import axios from "axios";
import MiniStatisticsCard from "@/examples/Cards/MiniStatisticsCard.vue";
import GradientLineChart from "@/examples/Charts/GradientLineChart.vue";
import Carousel from "./components/Carousel.vue";
import PieChart from "@/examples/Charts/PieChart.vue";

const dashboardData = ref(null);
const dashboardCards = ref([]);
const transactions = ref([]);
const currentPage = ref(0);
const totalPages = ref(1);
const limit = 7;
const token = ref("");
const loading = ref(true);
let refreshInterval;

// Retry helper
const retryRequest = async (fn, retries = 3, delay = 1000) => {
  try {
    return await fn();
  } catch (err) {
    if (retries === 0) throw err;
    await new Promise((res) => setTimeout(res, delay));
    return retryRequest(fn, retries - 1, delay * 2); // exponential backoff
  }
};

const formatDate = (datetime) => new Date(datetime).toLocaleDateString();
const formatTime = (datetime) => new Date(datetime).toLocaleTimeString();

const fetchTransactions = async (page = 0) => {
  if (!token.value) return;
  try {
    const res = await axios.get(
      `https://posbackend.santimpay.net/transactions?limit=${limit}&page=${page}`,
      {
        headers: {
          Authorization: `Bearer ${token.value}`,
        },
      }
    );
    transactions.value = res.data.rows;
    currentPage.value = res.data.page;
    totalPages.value = res.data.totalPages;
  } catch (error) {
    console.error("Error fetching transactions:", error);
  }
};

const transactionStatusChart = ref({
  labels: ["Pending", "Failed", "Success"],
  datasets: [{ label: "Transaction Count", data: [] }],
});
const transactionSourceChart = ref({
  labels: ["Gateway", "Purchase", "Void"],
  datasets: [{ label: "Transaction Amount ($)", data: [] }],
});
const transactionPieChart = ref({
  labels: ["Gateway", "Purchase"],
  datasets: [{ label: "Amount", backgroundColor: ["#4BB543", "#f6c23e"], data: [0, 0] }],
});

const fetchDashboardData = async () => {
  if (!token.value) return;
  try {
    loading.value = true;

    const dashboardRes = await axios.get("https://posbackend.santimpay.net/dashboard", {
      headers: { Authorization: `Bearer ${token.value}` },
    });
    dashboardData.value = dashboardRes.data;

    dashboardCards.value = [
      {
        title: "Merchants",
        value: `${Number(dashboardData.value.merchants).toLocaleString()}`,
        description: "Total merchants",
        icon: { component: "ni ni-shop", background: "bg-gradient-primary", shape: "rounded-circle" },
      },
      {
        title: "Terminals",
        value: `${Number(dashboardData.value.terminals).toLocaleString()}`,
        description: "Active terminals",
        icon: { component: "ni ni-settings-gear-65", background: "bg-gradient-danger", shape: "rounded-circle" },
      },
      {
        title: "Success Transactions",
        value: `${Number(dashboardData.value.successTransactions).toLocaleString()}`,
        description: "Successful txs",
        icon: { component: "ni ni-check-bold", background: "bg-gradient-success", shape: "rounded-circle" },
      },
      {
        title: "Gateway Tx Count",
        value: `${Number(dashboardData.value.gatewayTransactionCount).toLocaleString()}`,
        description: "Gateway txs",
        icon: { component: "ni ni-world", background: "bg-gradient-danger", shape: "rounded-circle" },
      },
      {
        title: "Gateway Total",
        value: `${Number(dashboardData.value.gatewayTotalAmount).toLocaleString()} ETB`,
        description: "Total amount",
        icon: { component: "ni ni-money-coins", background: "bg-gradient-info", shape: "rounded-circle" },
      },
      {
        title: "Purchase Tx Count",
        value: `${Number(dashboardData.value.purchaseTransactionCount).toLocaleString()}`,
        description: "Purchase txns",
        icon: { component: "ni ni-cart", background: "bg-gradient-warning", shape: "rounded-circle" },
      },
      {
        title: "Purchase Total",
        value: `${Number(dashboardData.value.purchaseTotalAmount).toLocaleString()} ETB`,
        description: "Total amount",
        icon: { component: "ni ni-tag", background: "bg-gradient-primary", shape: "rounded-circle" },
      },
      {
        title: "Transaction Volume",
        value: {
          text: `${Number(dashboardData.value.transactionAmount).toLocaleString()} ETB`,
          color: "santim-yellow",
        },
        description: "Overall amount",
        icon: {
          component: "ni ni-credit-card",
          background: "bg-gradient-success",
          shape: "rounded-circle",
        },
        classContent: "highlight-volume",
      },
    ];

    transactionStatusChart.value.datasets[0].data = [
      dashboardData.value.pendingTransactions,
      dashboardData.value.failedTransactions,
      dashboardData.value.successTransactions,
    ];

    transactionSourceChart.value.datasets[0].data = [
      Number(dashboardData.value.gatewayTotalAmount),
      Number(dashboardData.value.purchaseTotalAmount),
      Number(dashboardData.value.voidTotalAmount),
    ];

    transactionPieChart.value.datasets[0].data = [
      Number(dashboardData.value.gatewayTotalAmount),
      Number(dashboardData.value.purchaseTotalAmount),
    ];

    await fetchTransactions(currentPage.value);
  } catch (err) {
    console.error("Error fetching dashboard data", err);
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  try {
    // 1. Authenticate once
    const authRes = await retryRequest(() =>
      axios.post("https://posbackend.santimpay.net/auth/default-user", {
        email: "admin1@gmail.com",
        password: "AdminPassword@1",
      })
    );
    token.value = authRes.data?.access_token;
    if (!token.value) throw new Error("Token not found");

    // 2. Fetch initial data
    await retryRequest(fetchDashboardData);

    // 3. Setup 5 min interval using existing token
    refreshInterval = setInterval(fetchDashboardData, 5 * 60 * 1000);
  } catch (err) {
    console.error("Authentication or initial fetch failed after retries:", err);
  }
});

onUnmounted(() => {
  clearInterval(refreshInterval);
});
</script>


<template>
  <div class="py-4 container-fluid">
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-warning mb-3" style="width: 3rem; height: 3rem;" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="fw-bold text-muted fs-5 animate__animated animate__pulse animate__infinite">
        Fetching latest dashboard data...
      </p>
    </div>
    <div v-else class="row">
      <div class="col-lg-12">
        <div class="row">
          <div v-for="(card, index) in dashboardCards" :key="index" class="col-lg-3 col-md-6 col-12">
            <mini-statistics-card :title="card.title" :value="card.value" :description="card.description"
              :icon="card.icon" />
          </div>
        </div>




        <!-- Graph -->
        <div class="row mt-2">
          <div class="col-lg-7 mb-lg">
            <div class="card z-index-2">
              <gradient-line-chart
                v-if="transactionStatusChart.datasets && transactionStatusChart.datasets[0].data.length"
                id="status-chart" title="Transaction Status" description="Success vs Failed vs Pending"
                :chart="transactionStatusChart" />
            </div>
          </div>
          <div class="col-lg-5">
            <carousel />
          </div>
        </div>



        <!-- Graph2 -->
        <div class="row mt-4">
          <div class="col-lg-5">
            <pie-chart id="transactionPieChart2" :chart-data="transactionPieChart" />
          </div>



          <div class="col-lg-7">
            <!-- Table -->
            <div class="row mt-4">
              <div class="table-responsive">
                <table class="table align-items-center mb-0">
                  <thead>
                    <tr>
                      <th>Merchant</th>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="txn in transactions" :key="txn.id">
                      <td>{{ txn.merchantName }}</td>
                      <td>{{ txn.transactionDate || formatDate(txn.created_at) }} {{ txn.transactionTime || formatTime(txn.created_at) }}</td>
                      <td>{{ txn.transactionType }}</td>
                      <td>
                        <span :class="{
                          'text-success': txn.status === 'SUCCESS',
                          'text-warning': txn.status === 'PENDING',
                          'text-danger': txn.status === 'FAILED'
                        }">
                          {{ txn.status }}
                        </span>
                      </td>
                      <td>{{ Number(txn.amount).toLocaleString() }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>





              <div class="d-flex justify-content-between align-items-center mt-3">
                <button class="btn btn-sm btn-outline-primary" :disabled="currentPage === 0"
                  @click="fetchTransactions(0)">
                  « First
                </button>

                <button class="btn btn-sm btn-outline-secondary" :disabled="currentPage === 0"
                  @click="fetchTransactions(currentPage - 1)">
                  ‹ Prev
                </button>

                <span class="mx-3">
                  Page {{ currentPage + 1 }} of {{ totalPages }}
                </span>

                <button class="btn btn-sm btn-outline-secondary" :disabled="currentPage >= totalPages - 1"
                  @click="fetchTransactions(currentPage + 1)">
                  Next ›
                </button>

                <button class="btn btn-sm btn-outline-primary" :disabled="currentPage >= totalPages - 1"
                  @click="fetchTransactions(totalPages - 1)">
                  Last »
                </button>
              </div>



            </div>
          </div>
        </div>





      </div>
    </div>
  </div>
</template>


<style scoped>
.santim-yellow {
  color: #e6b800 !important;
  font-weight: bold;
}


/* .santim-yellow {
  color: #e69500 !important;
  font-weight: bold;
} */


</style>